import { Box, Skeleton, SxProps } from "@mui/material";
import { bbox } from "@turf/turf";
import mapboxgl, { LngLatBoundsLike } from "mapbox-gl";
import { useEffect, useMemo, useRef, useState } from "react";

import { DEFAULT_MAPBOX_CONFIG } from "~/modules/map/mapConstants";
import generateDefaultMapStyle from "~/modules/mapEditor/components/MapEditorMapView/generateDefaultMapStyle";

const MINI_MAP_SOURCE_ID = "minimap-data";

const MINI_MAP_PADDING = 32;

const MINI_MAP_LAYERS = generateDefaultMapStyle(MINI_MAP_SOURCE_ID, "green", false);

interface GaiaMiniMapProps {
    geometry: GeoJSON.Geometry;
    sx?: SxProps;
}

export function GaiaMiniMap({ geometry, sx }: GaiaMiniMapProps) {
    const mapElementRef$ = useRef<HTMLDivElement>(null);
    const mapboxInstanceRef = useRef<mapboxgl.Map | null>(null);
    const [isInView, setIsInView] = useState(false);
    const [isMapLoaded, setIsMapLoaded] = useState(false);

    const bounds = useMemo(() => bbox(geometry) as LngLatBoundsLike, [geometry]);

    // Keep the map alive only while it's near the viewport. Otherwise remove
    useEffect(() => {
        const element = mapElementRef$.current;
        if (element === null) return;

        const observer = new IntersectionObserver(
            entries => {
                setIsInView(entries.some(entry => entry.isIntersecting));
            },
            { rootMargin: "200px" },
        );
        observer.observe(element);

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (!isInView) return;
        if (mapboxInstanceRef.current !== null || mapElementRef$.current === null) {
            return;
        }

        setIsMapLoaded(false);

        // Create fresh mapbox instance
        mapboxInstanceRef.current = new mapboxgl.Map({
            ...DEFAULT_MAPBOX_CONFIG,
            interactive: false,
            attributionControl: false,
            container: mapElementRef$.current,
            bounds,
            fitBoundsOptions: {
                padding: MINI_MAP_PADDING,
                maxZoom: 18,
            },
        });

        // Compact attribution - Mapbox's ToS requires it - moved to the top right corner of the map
        mapboxInstanceRef.current.addControl(new mapboxgl.AttributionControl({ compact: true }), "top-right");

        const onStyleLoad = () => {
            const map = mapboxInstanceRef.current;
            if (map === null) return;

            map.addSource(MINI_MAP_SOURCE_ID, {
                type: "geojson",
                data: geometry,
            });

            MINI_MAP_LAYERS.forEach(config => {
                map.addLayer(config);
            });

            setIsMapLoaded(true);
        };

        mapboxInstanceRef.current.on("style.load", onStyleLoad);

        return () => {
            mapboxInstanceRef.current?.off("style.load", onStyleLoad).remove();
            mapboxInstanceRef.current = null;
            setIsMapLoaded(false);
        };
    }, [bounds, geometry, isInView]);

    return (
        <Box sx={{ ...sx, overflow: "hidden", position: "relative" }}>
            <Box ref={mapElementRef$} sx={{ width: 1, height: 1, position: "relative" }} />
            {!isMapLoaded && (
                <Skeleton variant="rectangular" sx={{ position: "absolute", inset: 0, pointerEvents: "none", height: "100%" }} />
            )}
        </Box>
    );
}
